// src/pages/Dashboard.tsx
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import { useDashboardCards } from "../hooks/useDashboardCards";
import { BackgroundGrid } from "../components/dashboard/BackgroundGrid";
import { DashboardHeader } from "../components/dashboard/DashboardHeader";
import { CardItem } from "../components/dashboard/CardItem";
import { LoadingSkeleton } from "../components/dashboard/LoadingSkeleton";
import { EmptyState } from "../components/dashboard/EmptyState";

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { cards, loading, loadingMore, hasMore, error, loadMore } =
    useDashboardCards(user?.id);

  const handleCardClick = useCallback((cardId: string) => {
    window.location.href = `/share/${cardId}`;
  }, []);

  const handleCreateClick = useCallback(() => {
    navigate("/archetypes");
  }, [navigate]);

  // Auth redirect
  if (!user) {
    navigate("/signin");
    return null;
  }

  return (
    <div className="min-h-screen bg-[#08080a] text-white selection:bg-yellow-400 flex flex-col overflow-x-hidden">
      <BackgroundGrid />
      <DashboardHeader onCreateClick={handleCreateClick} />

      <main className="relative z-10 flex-1 py-10 px-6 max-w-[1400px] w-full mx-auto">
        {loading ? (
          <LoadingSkeleton />
        ) : error ? (
          <div className="text-center text-red-400">
            Failed to load cards. Please try again.
          </div>
        ) : cards.length === 0 ? (
          <EmptyState onCreateClick={handleCreateClick} />
        ) : (
          <div className="space-y-12">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-y-12 gap-x-6">
              {cards.map((card, index) => (
                <CardItem
                  key={card.id}
                  card={card}
                  index={index}
                  onClick={handleCardClick}
                />
              ))}
            </div>

            {hasMore && (
              <div className="flex justify-center pt-8">
                <button
                  onClick={loadMore}
                  disabled={loadingMore}
                  className="px-8 py-4 border border-white/10 rounded-2xl text-[10px] uppercase tracking-[0.3em] font-black hover:bg-white hover:text-black transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label="Load more assets"
                >
                  {loadingMore ? (
                    <Loader2 className="animate-spin" size={20} />
                  ) : (
                    "Load More Assets"
                  )}
                </button>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
